import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextFieldProps,
    FormControl,
    InputLabel,
    FormControlProps,
    Select,
    MenuItem,
    FormHelperText,
    GridLegacy as Grid,
    SelectChangeEvent
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateCompanyAccountTransactionRequest } from '@/models/payment';
import { Validator } from '@/utils/errorHandling';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { CompanyAccount, CompanyAccountsResponse } from '@/models/company';
import { AccountUpdateResult } from './AccountUpdateResult';
import { NumericInput } from '@/components/shared/NumericInput';
import {
    PrimaryButton,
    SecondaryButtonRegular
} from '@/components/shared/Button';

export type AddCompanyAccountTransactionDialogMode = 'Deposit' | 'Withdraw';

type AddCompanyAccountTransactionDialogProps = {
    open: boolean;
    mode: AddCompanyAccountTransactionDialogMode;
    companyAccounts: CompanyAccountsResponse;
    onClose: () => void;
    onSubmit: (request: CreateCompanyAccountTransactionRequest) => void;
    isSubmitting: boolean;
    validator: Validator;
    setValidator: React.Dispatch<React.SetStateAction<Validator>>;
};

export const AddCompanyAccountTransactionDialog = (
    props: AddCompanyAccountTransactionDialogProps
) => {
    // Copy before sorting — the array is shared React Query cache data and
    // Array.prototype.sort mutates in place.
    const sortedCompanyAccounts = [
        ...props.companyAccounts.companyAccounts
    ].sort((a, b) => a.name?.localeCompare(b.name));

    const defaultCompanyAccount =
        sortedCompanyAccounts.length > 0 ? sortedCompanyAccounts[0] : null;

    const [companyId, setCompanyId] = useState<string>(
        defaultCompanyAccount?.companyId ?? ''
    );
    const [companyAccount, setCompanyAccount] = useState<CompanyAccount | null>(
        defaultCompanyAccount
    );
    const [amount, setAmount] = useState<number | null>(null);
    const [hasFormatError, setHasFormatError] = useState(false);
    const { setAlert } = useNotificationsSnackbar();
    const { t } = useTranslation();

    // The Dialog stays mounted, so re-default the form every time it opens —
    // otherwise the previous submission's values would linger on reopen.
    useEffect(() => {
        if (props.open) {
            setCompanyId(defaultCompanyAccount?.companyId ?? '');
            setCompanyAccount(defaultCompanyAccount);
            setAmount(null);
            setHasFormatError(false);
        }
    }, [props.open]);

    const onSubmit = () => {
        const amountDecimal = amount;

        if (amountDecimal === null || amountDecimal === undefined) {
            setAlert(t('company_accounts.amount_must_be_number'), 'error');
            return;
        }

        if (!companyAccount) {
            setAlert(t('company_accounts.please_select_company'), 'error');
            return;
        }
        const amountSigned =
            props.mode === 'Deposit' ? amountDecimal : amountDecimal * -1;

        if (
            props.mode === 'Withdraw' &&
            companyAccount.balance + amountSigned < 0
        ) {
            setAlert(
                `You cannot withdraw more than ${companyAccount.balance} ${companyAccount.currency} - the new balance has to be >= zero.`,
                'error'
            );
            return;
        }

        props.onSubmit({
            companyId: companyAccount?.companyId,
            amount: amountSigned
        });
    };

    const getAmountNumber = () => {
        return amount ?? 0;
    };

    const onCompanyAccountChange = (e: SelectChangeEvent<string>) => {
        setCompanyId(e.target.value);
        const companyAccount = sortedCompanyAccounts.find(
            (_) => _.companyId === e.target.value
        );
        setCompanyAccount(companyAccount ?? null);
        props.setValidator(props.validator.clearError('companyId'));
    };

    const commonProps: TextFieldProps = {
        margin: 'dense',
        fullWidth: true,
        variant: 'standard'
    };

    return (
        <>
            {sortedCompanyAccounts.length > 0 && (
                <Dialog open={props.open} onClose={props.onClose}>
                    <DialogTitle>{props.mode}</DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <FormControl
                                    {...(commonProps as FormControlProps)}
                                >
                                    <InputLabel>
                                        {t('company_accounts.company')}
                                    </InputLabel>
                                    <Select
                                        variant="standard"
                                        value={companyId}
                                        label={t('company_accounts.company')}
                                        error={props.validator.hasError(
                                            'companyId'
                                        )}
                                        onChange={onCompanyAccountChange}
                                    >
                                        {sortedCompanyAccounts.map(
                                            (companyAccount) => (
                                                <MenuItem
                                                    key={
                                                        companyAccount.companyId
                                                    }
                                                    value={
                                                        companyAccount.companyId
                                                    }
                                                >
                                                    {companyAccount.name}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                    <FormHelperText>
                                        {props.validator.getError('loginType')}
                                    </FormHelperText>
                                </FormControl>

                                <NumericInput
                                    error={props.validator.hasError('amount')}
                                    helperText={props.validator.getError(
                                        'amount'
                                    )}
                                    label={t('company_accounts.amount')}
                                    value={amount}
                                    onChange={(val) => {
                                        setAmount(val);
                                        props.setValidator(
                                            props.validator.clearError('amount')
                                        );
                                    }}
                                    onFormatError={setHasFormatError}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                {companyAccount && (
                                    <AccountUpdateResult
                                        currency={companyAccount.currency}
                                        balance={companyAccount.balance}
                                        amount={getAmountNumber()}
                                        mode={props.mode}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <SecondaryButtonRegular
                            onClick={props.onClose}
                            disabled={props.isSubmitting}
                        >
                            {t('cancel')}
                        </SecondaryButtonRegular>
                        <PrimaryButton
                            onClick={onSubmit}
                            disabled={hasFormatError || props.isSubmitting}
                        >
                            {props.mode}
                        </PrimaryButton>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};
